import React, { useRef, useEffect, useState } from 'react';
import { X } from 'lucide-react';

// Bottom-sheet canvas signature pad. Canvas pixel size is set to match its
// container's actual CSS size (measured on mount) so pointer coordinates line
// up 1:1 with drawn strokes — no width/height style scaling that would blur
// or misalign strokes on different screen widths.
export default function SignaturePad({ value, onSave, onClose }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [isEmpty, setIsEmpty] = useState(!value);

  useEffect(() => {
    const canvas = canvasRef.current;
    const width = containerRef.current.clientWidth;
    canvas.width = width;
    canvas.height = 160;
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#111';
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, width, 160);
      img.src = value;
    }
  }, [value]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineTo(x, y);
    ctx.stroke();
    setIsEmpty(false);
  };

  const end = () => { drawing.current = false; };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setIsEmpty(true);
  };

  const save = () => {
    onSave(isEmpty ? null : canvasRef.current.toDataURL('image/png'));
    onClose();
  };

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,.5)', display: 'flex',
      alignItems: 'flex-end', justifyContent: 'center', zIndex: 100
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: '#fff', borderRadius: '16px 16px 0 0', padding: 18, width: '100%', maxWidth: 480
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ fontWeight: 700 }}>Customer Signature</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>
        <div ref={containerRef} style={{ border: '1px dashed var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <canvas
            ref={canvasRef}
            style={{ display: 'block', touchAction: 'none', background: '#fff' }}
            onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
            onTouchStart={start} onTouchMove={move} onTouchEnd={end}
          />
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
          <button onClick={clear} style={{ flex: 1, background: '#fff', color: 'var(--bg-sidebar)', border: '1.5px solid var(--bg-sidebar)', borderRadius: 22, padding: '10px 0', fontWeight: 700, cursor: 'pointer' }}>
            Clear
          </button>
          <button onClick={save} style={{ flex: 1, background: 'var(--bg-sidebar)', color: '#fff', border: 'none', borderRadius: 22, padding: '10px 0', fontWeight: 700, cursor: 'pointer' }}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
